/**
 * Settings ??Checklist templates
 * SS cards ??equipment categories ??question editor (add / edit / delete).
 * Yellow SS card = pending field change requests.
 */
import { useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useAuthStore } from '../../stores/authStore';
import ModulePageHeading from '../../components/ModulePageHeading';
import { resolveSettingsNav } from '../../navigation/settingsNavigation';
import ChecklistSetEditor from '../../components/ChecklistSetEditor';
import { sortBySsCode } from '../../utils/ssCodeSort';

function ChecklistTemplates() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const ssCode = searchParams.get('ss') || '';
  const unitType = searchParams.get('cat') || '';
  const editSsDefault = searchParams.get('scope') === 'SS_DEFAULT';
  const [showRequests, setShowRequests] = useState(false);
  const [newCatLabel, setNewCatLabel] = useState('');
  const [newCatDesc, setNewCatDesc] = useState('');
  const [showAddCat, setShowAddCat] = useState(false);
  const [editCat, setEditCat] = useState(null); // { category_key, label, description }
  const [editSs, setEditSs] = useState(null); // { ss_code, name }

  const { data: ssCardsRaw = [], isLoading: loadingCards } = useQuery({
    queryKey: ['checklist-ss-cards'],
    queryFn: async () => {
      const res = await api.get('/checklist-sets/ss-cards');
      return res.data?.data ?? [];
    },
  });
  const ssCards = useMemo(() => sortBySsCode(ssCardsRaw), [ssCardsRaw]);

  const { data: categories = [], isLoading: loadingCats } = useQuery({
    queryKey: ['checklist-categories', ssCode],
    queryFn: async () => {
      const res = await api.get('/checklist-sets/categories', { params: { ssCode } });
      return res.data?.data ?? [];
    },
    enabled: !!ssCode,
  });

  const { data: pendingRequests = [] } = useQuery({
    queryKey: ['checklist-change-requests', ssCode || 'all', unitType || ''],
    queryFn: async () => {
      const res = await api.get('/checklist-sets/change-requests', {
        params: {
          ssCode: ssCode || undefined,
          unitType: unitType || undefined,
        },
      });
      return res.data?.data ?? [];
    },
  });

  const totalPending = useMemo(
    () => ssCards.reduce((n, c) => n + (c.pending_requests || 0), 0),
    [ssCards]
  );

  const selectedSs = useMemo(
    () => ssCards.find((c) => c.ss_code === ssCode) || null,
    [ssCards, ssCode]
  );
  const ssHeading = selectedSs
    ? [selectedSs.ss_code, selectedSs.name].filter(Boolean).join(' ')
    : ssCode;

  const reviewMutation = useMutation({
    mutationFn: ({ id, approve }) =>
      api.post(`/checklist-sets/change-requests/${id}/review`, { approve }),
    onSuccess: (_r, vars) => {
      toast.success(vars.approve ? 'Request approved ??checklist updated' : 'Request rejected');
      queryClient.invalidateQueries({ queryKey: ['checklist-change-requests'] });
      queryClient.invalidateQueries({ queryKey: ['checklist-ss-cards'] });
      queryClient.invalidateQueries({ queryKey: ['checklist-categories'] });
      queryClient.invalidateQueries({ queryKey: ['checklist-set'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Review failed'),
  });

  const addCategoryMutation = useMutation({
    mutationFn: () =>
      api.post('/checklist-sets/categories', {
        ssCode,
        label: newCatLabel.trim(),
        description: newCatDesc.trim() || undefined,
      }),
    onSuccess: (res) => {
      const label = res.data?.data?.label || newCatLabel.trim();
      toast.success(`Category "{label}"added`);
      setNewCatLabel('');
      setNewCatDesc('');
      setShowAddCat(false);
      queryClient.invalidateQueries({ queryKey: ['checklist-categories', ssCode] });
      queryClient.invalidateQueries({ queryKey: ['checklist-ss-cards'] });
      openCat(label);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Could not add category'),
  });

  const updateCategoryMutation = useMutation({
    mutationFn: (payload) => api.post('/checklist-sets/categories/update', payload),
    onSuccess: (res) => {
      const updated = res.data?.data;
      if (!updated?.label) {
        toast.error('Update did not return saved data ??try again after refresh');
        return;
      }
      toast.success('Category updated');
      setEditCat(null);
      queryClient.setQueryData(['checklist-categories', ssCode], (old) => {
        if (!Array.isArray(old)) return old;
        return old.map((c) =>
          c.category_key === updated.category_key
            ? { ...c, label: updated.label, description: updated.description }
            : c
        );
      });
      queryClient.invalidateQueries({ queryKey: ['checklist-categories', ssCode] });
      queryClient.invalidateQueries({ queryKey: ['checklist-ss-cards'] });
      queryClient.invalidateQueries({ queryKey: ['checklist-set'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Could not update category'),
  });

  const updateSsMutation = useMutation({
    mutationFn: (payload) => api.post('/checklist-sets/ss-cards/update', payload),
    onSuccess: (res) => {
      const updated = res.data?.data;
      if (!updated?.name) {
        toast.error('Update did not return saved data ??try again after refresh');
        return;
      }
      toast.success('SS card updated');
      setEditSs(null);
      queryClient.setQueryData(['checklist-ss-cards'], (old) => {
        if (!Array.isArray(old)) return old;
        return old.map((c) =>
          c.ss_code === updated.ss_code
            ? { ...c, name: updated.name, description: updated.description }
            : c
        );
      });
      queryClient.invalidateQueries({ queryKey: ['checklist-ss-cards'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Could not update SS card'),
  });

  const openSs = (code) => {
    setSearchParams({ ss: code });
    setShowRequests(false);
  };

  const openSsDefault = () => {
    setSearchParams({ ss: ssCode, scope: 'SS_DEFAULT' });
  };

  const openCat = (label) => {
    setSearchParams({ ss: ssCode, cat: label });
  };

  const back = () => {
    if (editSsDefault) setSearchParams({ ss: ssCode });
    else if (unitType) setSearchParams({ ss: ssCode });
    else if (ssCode) setSearchParams({});
  };

  if (user?.role === 'WORKER') return <Navigate to="/" replace />;

  return (
    <div>
      <ModulePageHeading
        resolveNav={resolveSettingsNav}
        title="Checklists"
        infoTitle="Checklist templates"
        infoChildren={
          <>
          <p className="mb-2">
            This is the <strong>master checklist library</strong>. Edit SS default questions (and attach the Compliance
            Schedule Handbook / manuals), then optional equipment-category variants. Publish/Approve updates apply to
            all buildings automatically on the next inspection.
          </p>
          <ul className="list-disc pl-5 space-y-1 text-sm">
            <li>Open an SS ??edit the <strong>SS default checklist</strong> first (Pass/Fail source for units).</li>
            <li>Add equipment categories for type-specific questions when needed.</li>
            <li>Yellow SS card = pending field add / edit / delete requests.</li>
            <li>On a building: add SS units (first inspection or new equipment) using these templates.</li>
            <li>Floor Plan and SS Units open the same category checklists when a unit has that type.</li>
          </ul>
          </>
        }
        actions={
          ssCode || unitType ? (
            <button type="button" onClick={back} className="text-sm text-primary-700 hover:underline">
              Back
            </button>
          ) : null
        }
      />

      {totalPending > 0 && (
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <Bell className="w-4 h-4 shrink-0" />
          <span>
            <strong>{totalPending}</strong> field request{totalPending === 1 ? '' : 's'} waiting for review.
            Yellow cards mark affected SS.
          </span>
          <button
            type="button"
            className="ml-auto text-amber-900 underline font-medium"
            onClick={() => setShowRequests(true)}
          >
            View requests
          </button>
        </div>
      )}

      {showRequests && (
        <div className="mb-6 rounded-lg border border-gray-200 bg-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-gray-900">Pending field requests</h2>
            <button type="button" className="text-sm text-gray-500" onClick={() => setShowRequests(false)}>
              Close
            </button>
          </div>
          {pendingRequests.length === 0 ? (
            <p className="text-sm text-gray-500">No pending requests for this filter.</p>
          ) : (
            <ul className="space-y-3">
              {pendingRequests.map((r) => (
                <li key={r.id} className="border border-gray-100 rounded-lg p-3 text-sm">
                  <div className="flex flex-wrap gap-2 items-baseline">
                    <span className="font-semibold">{r.ss_code}</span>
                    <span className="text-gray-500">{r.unit_type || '—'}</span>
                    <span className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-800 text-xs">
                      {r.request_type}
                    </span>
                    <span className="text-xs text-amber-700">Field</span>
                  </div>
                  {r.note && <p className="mt-1 text-gray-600">{r.note}</p>}
                  <p className="text-xs text-gray-400 mt-1">
                    By {r.requested_by_name || r.requested_by || '—'} · {r.created_at}
                  </p>
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      className="text-xs px-2 py-1 bg-emerald-600 text-white rounded"
                      disabled={reviewMutation.isPending}
                      onClick={() => {
                        setSearchParams({
                          ss: r.ss_code,
                          ...(r.unit_type ? { cat: r.unit_type } : {}),
                        });
                        reviewMutation.mutate({ id: r.id, approve: true });
                      }}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="text-xs px-2 py-1 border border-gray-300 rounded"
                      disabled={reviewMutation.isPending}
                      onClick={() => reviewMutation.mutate({ id: r.id, approve: false })}
                    >
                      Reject
                    </button>
                    <button
                      type="button"
                      className="text-xs px-2 py-1 text-primary-700 underline"
                      onClick={() => {
                        setSearchParams({
                          ss: r.ss_code,
                          ...(r.unit_type ? { cat: r.unit_type } : {}),
                        });
                        setShowRequests(false);
                      }}
                    >
                      Open category
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Level 0 ??SS cards */}
      {!ssCode && (
        <>
          {loadingCards ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {ssCards.map((c) => (
                <div
                  key={c.ss_code}
                  className={`relative aspect-square rounded-lg border-2 text-left transition-colors ${
                    c.has_pending
                      ? 'border-amber-400 bg-amber-50'
                      : 'border-gray-200 bg-white hover:border-primary-300 hover:bg-primary-50/40'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => openSs(c.ss_code)}
                    className="absolute inset-0 p-3 pr-10 text-left flex flex-col justify-between rounded-lg"
                  >
                    <span className="text-sm font-bold text-gray-900 leading-tight">{c.ss_code}</span>
                    <span className="text-[11px] text-gray-600 line-clamp-3 leading-snug">{c.name}</span>
                    <span className="text-[10px] text-gray-400">
                      {c.category_count} categor{c.category_count === 1 ? 'y' : 'ies'}
                      {c.has_pending ? ` · ${c.pending_requests} request${c.pending_requests === 1 ? '' : 's'}` : ''}
                    </span>
                  </button>
                  <button
                    type="button"
                    title="Edit name under SS code"
                    aria-label={`Edit ${c.ss_code}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setEditSs({ ss_code: c.ss_code, name: c.name || '' });
                    }}
                    className="absolute top-1.5 right-1.5 z-20 p-2 rounded-md bg-white/95 text-gray-600 shadow-sm border border-gray-200 hover:text-primary-700 hover:border-primary-300"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Level 1 ??categories + SS default entry */}
      {ssCode && !unitType && !editSsDefault && (
        <>
          <h2 className="text-xl font-bold text-gray-900 leading-tight mb-0.5">{ssHeading}</h2>
          <p className="text-sm text-gray-600 mb-4">
            Start with the SS default checklist (master for Pass/Fail). Then open an equipment category if that plant
            needs different questions.
          </p>

          <button
            type="button"
            onClick={openSsDefault}
            className="mb-4 w-full sm:w-auto text-left rounded-lg border-2 border-primary-300 bg-primary-50 px-4 py-3 hover:bg-primary-100/80"
          >
            <span className="block text-sm font-semibold text-primary-900">Edit SS default checklist</span>
            <span className="block text-xs text-primary-800 mt-0.5">
              Questions + manuals / handbook ??published version auto-updates all building inspections
            </span>
          </button>

          <p className="text-sm font-medium text-gray-700 mb-2">Equipment categories (unit types)</p>
          {loadingCats ? (
            <p className="text-sm text-gray-500">Loading categories…</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {categories.map((c) => (
                <div
                  key={c.category_key}
                  className={`relative min-h-[6.5rem] rounded-lg border-2 transition-colors ${
                    c.has_pending
                      ? 'border-amber-400 bg-amber-50'
                      : 'border-gray-200 bg-white hover:border-violet-300 hover:bg-violet-50/50'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => openCat(c.label)}
                    className="absolute inset-0 p-3 pr-10 text-left flex flex-col justify-between rounded-lg"
                  >
                    <span className="text-sm font-semibold text-gray-900">{c.label}</span>
                    {c.description ? (
                      <span className="text-[11px] text-gray-500 line-clamp-2 mt-1">{c.description}</span>
                    ) : (
                      <span className="text-[11px] text-gray-400 italic mt-1">No description</span>
                    )}
                    {c.has_pending && (
                      <span className="text-[10px] text-amber-800 mt-2">{c.pending_requests} pending</span>
                    )}
                  </button>
                  <button
                    type="button"
                    title="Edit name & description"
                    aria-label={`Edit ${c.label}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowAddCat(false);
                      setEditCat({
                        category_key: c.category_key,
                        label: c.label || '',
                        description: c.description || '',
                      });
                    }}
                    className="absolute top-1.5 right-1.5 z-20 p-2 rounded-md bg-white/95 text-gray-600 shadow-sm border border-gray-200 hover:text-primary-700 hover:border-primary-300"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  setEditCat(null);
                  setShowAddCat(true);
                }}
                className="min-h-[6.5rem] rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-3 text-sm text-gray-600 hover:border-primary-400 hover:text-primary-800 hover:bg-primary-50/40"
              >
                + Add category
              </button>
            </div>
          )}

          {showAddCat && (
            <div className="mt-4 max-w-md rounded-lg border border-gray-200 bg-card p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">New equipment category</h3>
              <p className="text-xs text-gray-500">
                For unusual or site-specific plant under {ssCode}. A starter checklist is created automatically.
              </p>
              <input
                type="text"
                value={newCatLabel}
                onChange={(e) => setNewCatLabel(e.target.value)}
                placeholder="e.g. Heat recovery ventilator, Kitchen make-up air"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              <input
                type="text"
                value={newCatDesc}
                onChange={(e) => setNewCatDesc(e.target.value)}
                placeholder="Short description (optional)"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg"
                  onClick={() => setShowAddCat(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!newCatLabel.trim() || addCategoryMutation.isPending}
                  className="text-sm px-3 py-1.5 bg-primary-600 text-white rounded-lg disabled:opacity-50"
                  onClick={() => addCategoryMutation.mutate()}
                >
                  Add &amp; open
                </button>
              </div>
            </div>
          )}

          {categories.some((c) => c.has_pending) && (
            <button
              type="button"
              className="mt-4 text-sm text-amber-800 underline"
              onClick={() => setShowRequests(true)}
            >
              Review requests for {ssCode}
            </button>
          )}
        </>
      )}

      {/* Level 2a ??SS default master editor */}
      {ssCode && editSsDefault && (
        <ChecklistSetEditor
          ssCode={ssCode}
          ssName={selectedSs?.name || ''}
          scope="SS_DEFAULT"
          title={`${ssHeading} ??SS default checklist`}
          subtitle="Master template for unit inspections. Attach handbook/manuals below. Publish to auto-update buildings."
        />
      )}

      {/* Level 2b ??category editor */}
      {ssCode && unitType && !editSsDefault && (
        <ChecklistSetEditor
          ssCode={ssCode}
          ssName={selectedSs?.name || ''}
          scope="UNIT_TYPE"
          unitType={unitType}
        />
      )}

      {editSs && (
        <div
          className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4"
          onClick={() => setEditSs(null)}
        >
          <div
            className="bg-card rounded-lg shadow-xl max-w-md w-full p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900">Edit SS card — {editSs.ss_code}</h3>
            <p className="text-xs text-gray-500">Name shown under the SS code on the card and in headings.</p>
            <textarea
              rows={3}
              value={editSs.name}
              onChange={(e) => setEditSs((p) => ({ ...p, name: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="e.g. Automatic sprinkler"
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg"
                onClick={() => setEditSs(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!editSs.name.trim() || updateSsMutation.isPending}
                className="text-sm px-3 py-1.5 bg-primary-600 text-white rounded-lg disabled:opacity-50"
                onClick={() =>
                  updateSsMutation.mutate({
                    ssCode: editSs.ss_code,
                    name: editSs.name.trim(),
                  })
                }
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {editCat && (
        <div
          className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4"
          onClick={() => setEditCat(null)}
        >
          <div
            className="bg-card rounded-lg shadow-xl max-w-md w-full p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900">Edit category card</h3>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
              <input
                type="text"
                value={editCat.label}
                onChange={(e) => setEditCat((p) => ({ ...p, label: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <textarea
                rows={3}
                value={editCat.description}
                onChange={(e) => setEditCat((p) => ({ ...p, description: e.target.value }))}
                placeholder="Short description under the card name"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg"
                onClick={() => setEditCat(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!editCat.label.trim() || updateCategoryMutation.isPending}
                className="text-sm px-3 py-1.5 bg-primary-600 text-white rounded-lg disabled:opacity-50"
                onClick={() =>
                  updateCategoryMutation.mutate({
                    ssCode,
                    categoryKey: editCat.category_key,
                    label: editCat.label.trim(),
                    description: editCat.description ?? '',
                  })
                }
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChecklistTemplates;
